import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { formatUnixDateTime } from '@/shared/lib/datetime';
import { toOptions } from '@/shared/lib/options';
import { DeleteButton } from '@/shared/ui/list/DeleteButton';
import { GenericListPage } from '@/shared/ui/list/GenericListPage';
import type { ListPageConfig, ListRow } from '@/shared/ui/list/types';
import { deleteRaffle, fetchRaffleList, fetchRaffleStatuses } from './api';

const LIST_KEY = 'raffle-list';

interface Raffle extends ListRow {
  id: number;
  name: string;
  status_id?: number;
  start_time?: number;
  end_time?: number;
  prizes?: Array<{ name: string }>;
}

const formatDay = (seconds?: number) => formatUnixDateTime(seconds).split(',')[0];

export const RaffleListPage = () => {
  const statuses = useQuery({ queryKey: ['raffle-statuses'], queryFn: fetchRaffleStatuses });

  const config = useMemo<ListPageConfig<Raffle>>(() => {
    const statusById = new Map((statuses.data ?? []).map((status) => [status.id, status]));

    return {
      queryKey: LIST_KEY,
      title: 'Розыгрыши',
      errorText: 'Ошибка загрузки розыгрышей',
      fetchList: fetchRaffleList,
      rows: ['data'],
      filters: [
        {
          name: 'status_id',
          label: 'Статус',
          kind: 'select',
          options: toOptions(statuses.data),
          clientOnly: true,
        },
      ],
      selectRows: (rows, { applied }) =>
        applied.status_id === ''
          ? rows
          : rows.filter((raffle) => String(raffle.status_id) === applied.status_id),
      columns: [
        {
          header: 'Название',
          render: (raffle) => <Link to={`/raffle/view/${raffle.id}`}>{raffle.name}</Link>,
        },
        {
          header: 'Дата проведения',
          render: (raffle) => `${formatDay(raffle.start_time)} - ${formatDay(raffle.end_time)}`,
        },
        {
          header: 'Призы',
          render: (raffle) => raffle.prizes?.map((prize) => prize.name).join(', '),
        },
        {
          header: 'Статус',
          render: (raffle) => {
            const status = statusById.get(Number(raffle.status_id));
            return (
              <span style={{ background: status?.color, padding: '4px 8px', display: 'inline-block' }}>
                {status?.name}
              </span>
            );
          },
        },
        {
          header: 'Действия',
          render: (raffle) => (
            <DeleteButton listKey={LIST_KEY} remove={() => deleteRaffle(raffle.id)} />
          ),
        },
      ],
    };
  }, [statuses.data]);

  return <GenericListPage config={config} />;
};
